import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '../../atoms/Button/Button.js';
import { CircleAlert, Info } from '../../icons.js';
import { cn } from '../../lib/cn.js';
import { failureKind, isRetryable, useFailureReason } from '../../lib/failure.js';
import { useTranslate } from '../../lib/i18n.js';
/**
 * A standing notice about the state of something, as opposed to feedback about
 * an action. (An action that *failed* is the exception: that is a state too —
 * the change has not happened — and `FailureNotice` below says it with this.)
 *
 * The distinction is why this exists beside `InlineConfirmation` and
 * `ErrorState`. A confirmation says "that worked" and goes away; an error state
 * says "this screen could not load" and replaces the content. A notice says
 * "your plan changed and here is what that means", stays on screen as long as
 * it is true, and sits *above* content that is still perfectly usable.
 *
 * Five of them appear on the subscription screen — lapsed access, a scheduled
 * change, being over the limit, practices students cannot open, a request
 * waiting for an answer — and every one of them was going to be a differently
 * bordered `div` otherwise.
 *
 * ── The roles ─────────────────────────────────────────────────────────────
 *
 * `role="status"` for information and `role="alert"` for a warning or an
 * error, which is the same split `StatusMessage` makes: an alert interrupts,
 * and a notice about something that has *already* happened to the account is
 * worth interrupting for.
 *
 * ── Colour never carries the meaning alone ───────────────────────────────
 *
 * Each tone has its own glyph as well as its own colour, for the reason
 * `FieldMessage` states: a reader who cannot tell amber from red still has to
 * be able to tell a warning from an error.
 */
export function Notice({ title, children, tone = 'info', actions, className }) {
    const Glyph = tone === 'info' ? Info : CircleAlert;
    return (_jsxs("div", { role: tone === 'info' ? 'status' : 'alert', className: cn('flex gap-3 rounded-md border p-4', tone === 'info' && 'border-border bg-surface-secondary', tone === 'warning' && 'border-warning/40 bg-warning/5', tone === 'error' && 'border-error-border bg-error-light', className), children: [_jsx(Glyph, { "aria-hidden": "true", className: cn('mt-0.5 size-icon-md shrink-0', tone === 'info' && 'text-text-secondary', tone === 'warning' && 'text-warning', tone === 'error' && 'text-error-text') }), _jsxs("div", { className: "flex min-w-0 flex-col gap-2", children: [_jsx("p", { className: cn('text-body font-semibold', tone === 'error' ? 'text-error-text' : 'text-text'), children: title }), children ? _jsx("div", { className: "text-body-sm text-text-secondary", children: children }) : null, actions ? _jsx("div", { className: "flex flex-wrap gap-2 pt-1", children: actions }) : null] })] }));
}
/**
 * An action the user took that did not complete — a save, an assignment, an
 * archive — said where they took it, and kept on screen until it is resolved.
 *
 * A `Notice`, because this is the same standing message about the state of
 * things: the change the user asked for has *not* happened, and that stays
 * true until it does. Not a toast, which would be gone before the user could
 * act on it, and not an error state, because the screen and everything the
 * user entered on it are still there.
 *
 * - **Tone.** A failure that trying again could fix — a dropped connection,
 *   the server stumbling — is a warning: nothing is lost, and red would say
 *   otherwise. One that will not go away by itself — signed out, not allowed,
 *   gone — is an error.
 * - **Retry.** Offered only when it could work, and only by a caller that
 *   knows how to repeat the action with what is on screen now.
 * - **Announcement.** `role="alert"`, like every non-info notice: the user
 *   pressed a button and has to be told it did not work. Focus stays where it
 *   was.
 */
export function FailureNotice({ title, error, onRetry, retrying, className }) {
    const t = useTranslate();
    const reason = useFailureReason();
    const retryable = isRetryable(failureKind(error));
    return (_jsx(Notice, { tone: retryable ? 'warning' : 'error', title: title, ...(className === undefined ? {} : { className }), ...(retryable && onRetry
            ? {
                actions: (_jsx(Button, { variant: "secondary", size: "sm", onClick: onRetry, loading: retrying ?? false, loadingText: t('ui.failure.retrying'), children: t('common.tryAgain') })),
            }
            : {}), children: reason(error) }));
}
//# sourceMappingURL=Notice.js.map